import React from "react";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch } from "../../../../../../redux";
import { getFileViewUrlSagaAction } from "../../../../../../redux/saga/onboarding-form/saga-actions";
import { useTimelineStepPermissions } from "../../../../../../hooks/useTimelineStepPermissions";
import { Integration, IntegrationDetail, EnvironmentEntry } from "../../../../../../redux/slices/projects/Types";
import {
  FormFieldConfig,
  getFormFields,
} from "../validation/components/modals/environmentFormConfig";
import EnvironmentDetailsModal from "../validation/components/modals/EnvironmentDetailsModal";
import { formatDateForDisplay } from "../integration-steps/shared/timeline-helpers";
import ProjectDetailsEditIcon from "./ProjectDetailsEditIcon";
import "./EnvironmentDetailsSection.scss";

type EnvironmentDetailsSectionProps = {
  technology: string;
  integration: Integration;
};

type EnvironmentSectionConfig = {
  title: string;
  panels: string[][];
};

const ENVIRONMENT_SECTIONS: EnvironmentSectionConfig[] = [
  {
    title: "Application Identity",
    panels: [["application_name", "api_app_name"]],
  },
  {
    title: "Team & Project Info",
    panels: [
      [
        "end_customer_name",
        "dev_team_size",
        "application_version",
        "dev_team_location",
      ],
    ],
  },
  {
    title: "Technical Stack",
    panels: [
      [
        "access_control_system",
        "hid_origo_version",
        "hid_origo_sdk_version",
        "programming_language",
        "hosting_environment",
        "development_tools",
        "third_party_libraries",
      ],
    ],
  },
  {
    title: "Deployment & Use Cases",
    panels: [
      [
        "deployment_method",
        "planned_production_launch",
        "application_distribution",
        "mobile_id_provisioning",
        "device_compatibility_info",
        "app_store_link",
      ],
      ["end_user_usecase", "sequence_flow_diagram"],
    ],
  },
];

const getFileNameFromKey = (fileKey: string): string => {
  const parts = fileKey.split("/");
  return parts[parts.length - 1] || fileKey;
};

const getFieldValue = (
  envData: EnvironmentEntry | undefined,
  field: FormFieldConfig
): string => {
  if (!envData) return "";
  const rawValue = envData[field.name as keyof EnvironmentEntry];

  if (field.type === "file") {
    return typeof rawValue === "string" && rawValue ? rawValue : "";
  }

  if (field.type === "date") {
    return (
      formatDateForDisplay(
        typeof rawValue === "string" ? rawValue : null,
        true
      ) || ""
    );
  }

  if (rawValue == null || rawValue === "") {
    return "";
  }

  return String(rawValue);
};

type EnvironmentFieldProps = {
  field: FormFieldConfig;
  envData: EnvironmentEntry | undefined;
};

const EnvironmentFileField: React.FC<{
  field: FormFieldConfig;
  fileKey: string;
}> = ({ field, fileKey }) => {
  const dispatch = useAppDispatch();
  const fileName = getFileNameFromKey(fileKey);
  const extension = fileName.includes(".")
    ? fileName.split(".").pop()?.toUpperCase() || "FILE"
    : "FILE";

  const handlePreview = () => {
    dispatch(getFileViewUrlSagaAction(fileKey, "view"));
  };

  return (
    <div className="environment-details-section__field environment-details-section__field--file">
      <span className="environment-details-section__label">{field.label}</span>
      <div className="environment-details-section__file-card">
        <div className="environment-details-section__file-info">
          <span className="environment-details-section__file-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path
                fill="currentColor"
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2 5 5h-5V4zM8 13h8v2H8v-2zm0 4h8v2H8v-2z"
              />
            </svg>
          </span>
          <div className="environment-details-section__file-copy">
            <span className="environment-details-section__file-name">{fileName}</span>
            <span className="environment-details-section__file-meta">{extension}</span>
          </div>
        </div>
        <button
          type="button"
          className="environment-details-section__file-download"
          onClick={handlePreview}
        >
          <Iconography icon="download" size={IconographySizes.Small} />
          Download
        </button>
      </div>
    </div>
  );
};

const EnvironmentField: React.FC<EnvironmentFieldProps> = ({
  field,
  envData,
}) => {
  const value = getFieldValue(envData, field);

  if (field.type === "file") {
    if (!value) {
      return (
        <div className="environment-details-section__field">
          <span className="environment-details-section__label">{field.label}</span>
          <span className="environment-details-section__value">—</span>
        </div>
      );
    }

    return <EnvironmentFileField field={field} fileKey={value} />;
  }

  return (
    <div className="environment-details-section__field">
      <span className="environment-details-section__label">{field.label}</span>
      <span className="environment-details-section__value">{value || "—"}</span>
    </div>
  );
};

const EnvironmentDetailsSection: React.FC<EnvironmentDetailsSectionProps> = ({
  technology,
  integration,
}) => {
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const { canEditTimeline } = useTimelineStepPermissions();
  const envData = integration.environments?.[0];
  const formFields = React.useMemo(
    () => getFormFields(technology, integration.integration),
    [technology, integration.integration]
  );

  const fieldsByName = React.useMemo(() => {
    return new Map(formFields.map((field) => [field.name, field]));
  }, [formFields]);

  const visibleSections = React.useMemo(() => {
    return ENVIRONMENT_SECTIONS.map((section) => ({
      title: section.title,
      panels: section.panels
        .map((panelFieldNames) =>
          panelFieldNames
            .map((fieldName) => fieldsByName.get(fieldName))
            .filter((field): field is FormFieldConfig => Boolean(field))
        )
        .filter((panel) => panel.length > 0),
    })).filter((section) => section.panels.length > 0);
  }, [fieldsByName]);

  return (
    <>
      <div className="environment-details-block">
        <div className="environment-details-block__header">
          <h3 className="environment-details-block__title">Environment Details</h3>
          {canEditTimeline ? (
            <button
              type="button"
              className="environment-details-block__edit"
              onClick={() => setIsEditOpen(true)}
            >
              <ProjectDetailsEditIcon className="environment-details-block__edit-icon" />
              Edit
            </button>
          ) : null}
        </div>

        <div className="environment-details-block__body">
          <div className="environment-details-section">
            {visibleSections.map((section) => (
              <div key={section.title} className="environment-details-section__group">
                <div className="environment-details-section__group-header">
                  <h4 className="environment-details-section__group-title">
                    {section.title}
                  </h4>
                  <span
                    className="environment-details-section__divider"
                    aria-hidden="true"
                  />
                </div>

                <div className="environment-details-section__panels">
                  {section.panels.map((panelFields, panelIndex) => (
                    <div
                      key={`${section.title}-${panelIndex}`}
                      className="environment-details-section__panel"
                    >
                      <div className="environment-details-section__grid">
                        {panelFields.map((field) => (
                          <EnvironmentField
                            key={field.name}
                            field={field}
                            envData={envData}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isEditOpen ? (
        <EnvironmentDetailsModal
          isHIDUser={false}
          onClose={() => setIsEditOpen(false)}
          technology={technology}
          integration={integration.integration}
          integrationId={integration.integration_id}
          existingData={integration as unknown as IntegrationDetail}
          skipProjectDetailsRefetch={false}
        />
      ) : null}
    </>
  );
};

export default EnvironmentDetailsSection;
